import { useEffect, useMemo, useState } from 'react';
import { Button, Icons, Input, Label, SingleCombobox } from '@commenda/kakashi';
import { useSalesTaxProductTaxCodes } from '@commenda/api';
import type { ILineItemInput } from '@commenda/api/new-sales-tax/calculate/calculate';

export function LineItemsEditor({
  value,
  onChange,
  corporationId,
}: {
  value?: ILineItemInput[];
  onChange: (items: ILineItemInput[]) => void;
  corporationId: number;
}) {
  const defaultItems = useMemo<ILineItemInput[]>(
    () => [
      {
        lineNumber: 1,
        pricePerUnit: 100,
        quantity: 1,
        discount: 0,
        productId: '',
      },
    ],
    []
  );

  const [items, setItems] = useState<ILineItemInput[]>(value && value.length > 0 ? value : defaultItems);

  const { data: taxCodes } = useSalesTaxProductTaxCodes({
    token: undefined,
    filters: { corporationId },
  });

  useEffect(() => {
    // Initialize parent with default if no value provided
    if (!value || value.length === 0) {
      onChange(defaultItems);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const update = (next: ILineItemInput[]) => {
    setItems(next);
    onChange(next);
  };

  const handleAdd = () => {
    const nextLineNumber = (items[items.length - 1]?.lineNumber || 0) + 1;
    update([
      ...items,
      {
        lineNumber: nextLineNumber,
        pricePerUnit: 0,
        quantity: 1,
        // taxCode intentionally unset by default
        discount: 0,
        productId: '',
      },
    ]);
  };

  const handleRemove = (index: number) => {
    const next = items.filter((_, i) => i !== index);
    update(next);
  };

  return (
    <div className="w-full flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <h3 className="txt-medium-medium text-text-primary">Line Items</h3>
        <Button size="sm" variant="secondary" onClick={handleAdd}>
          <Icons.Plus className="size-4" />
          Add item
        </Button>
      </div>

      <div className="flex flex-col gap-3">
        {items.map((item, idx) => (
          <div key={item.lineNumber} className="rounded border border-border-subtle p-3 flex flex-col gap-2">
            <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
              <div className="flex flex-col gap-1">
                <Label>Line number</Label>
                <Input
                  type="number"
                  value={item.lineNumber}
                  onChange={(e) => {
                    const next = [...items];
                    next[idx] = {
                      ...next[idx],
                      lineNumber: Number(e.target.value),
                    };
                    update(next);
                  }}
                />
              </div>
              <div className="flex flex-col gap-1">
                <Label>Amount</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={item.pricePerUnit}
                  onChange={(e) => {
                    const next = [...items];
                    next[idx] = {
                      ...next[idx],
                      pricePerUnit: Number(e.target.value),
                    };
                    update(next);
                  }}
                />
              </div>
              <div className="flex flex-col gap-1">
                <Label>Quantity</Label>
                <Input
                  type="number"
                  value={item.quantity}
                  onChange={(e) => {
                    const next = [...items];
                    next[idx] = {
                      ...next[idx],
                      quantity: Number(e.target.value),
                    };
                    update(next);
                  }}
                />
              </div>
              <div className="flex flex-col gap-1">
                <Label>Tax code</Label>
                <SingleCombobox
                  options={
                    (taxCodes || []).map((code: { code: string }) => ({
                      value: code.code,
                      label: code.code.replaceAll('_', ' '),
                    }))
                  }
                  value={item.taxCode ?? ''}
                  setValue={(val) => {
                    const next = [...items];
                    next[idx] = {
                      ...next[idx],
                      taxCode: val || undefined,
                    };
                    update(next);
                  }}
                  placeholder="Select tax code (optional)"
                  _className="txt-small-medium"
                  canClearSelection
                />
              </div>
              <div className="flex flex-col gap-1">
                <Label>Discount</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={item.discount ?? 0}
                  onChange={(e) => {
                    const next = [...items];
                    next[idx] = {
                      ...next[idx],
                      discount: Number(e.target.value),
                    };
                    update(next);
                  }}
                />
              </div>
              <div className="flex flex-col gap-1">
                <Label>Product ID</Label>
                <Input
                  value={item.productId || ''}
                  onChange={(e) => {
                    const next = [...items];
                    next[idx] = {
                      ...next[idx],
                      productId: e.target.value,
                    };
                    update(next);
                  }}
                />
              </div>
            </div>
            <div className="flex justify-end">
              <Button size="sm" variant="tertiary" onClick={() => handleRemove(idx)}>
                <Icons.Trash01 className="size-4" />
                Remove
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


